import { useLayoutEffect, useRef } from "react";
import gsap from "gsap";

const prefersReduced = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// the source layer is sized to fill the headline it hides, so the two always
// scale together. CODE_LINE_HEIGHT must match the .hero-code rule in index.css.
const CODE_LINE_HEIGHT = 1.3;
const CODE_FILL = 0.98; // fraction of the headline height the source spans

// The hero's falling dimension line. The headline, its source, and the line all
// live inside one [data-hero-unit] group, so this measures and draws everything
// in the unit's own coordinates — the peel clip and the line share a space and
// stay aligned however the unit is transformed. The panel strip camera-locks the
// whole unit; navigation drives this through the returned handle. onReady fires
// once measured (a home first-load also plays the intro then).
export function useHeroLine({ areaRef, stripRef, panelRefs, camRef, activeRef, onReady }) {
  const geom = useRef({ vw: 0, x0: 0, x1: 0, xm: 0, y: 0, sky: 500, ux: 0, uy: 0 });
  const dropY = useRef({ v: -500 });
  const bend = useRef({ v: 0 });   // interactive peel + reveal driver
  const flex = useRef({ v: 0 });   // landing twang, visual only (kept out of the peel)
  const ctrlX = useRef({ v: 0 });  // bend control point, follows the cursor's x
  const unitRef = useRef(null);    // the hero group (headline + source + line)
  const gRef = useRef(null);       // line's <g> (drop transform)
  const pathRef = useRef(null);    // line path
  const capLRef = useRef(null);
  const capRRef = useRef(null);
  const hlRef = useRef(null);      // headline element (peel target)
  const eyebrowRef = useRef(null); // "01 / Purpose" eyebrow, wiped in by the line
  const codeRef = useRef(null);    // source layer behind the headline
  const wordsRef = useRef(null);   // per-word spans, for the shuffle-in
  const interactiveRef = useRef(false);
  const connectedRef = useRef(false);
  const lineTl = useRef(null);

  const draw = () => {
    const g = geom.current;
    if (!g.vw) return;
    // the strip pans the whole hero (unit + line) at camera rate; the line's own
    // group only carries the vertical drop
    if (stripRef.current) stripRef.current.style.transform = `translate3d(${-camRef.current.w}px,0,0)`;
    if (gRef.current) gRef.current.setAttribute("transform", `translate(0 ${dropY.current.v})`);
    const cx = ctrlX.current.v || g.xm;
    // round the vertical to whole pixels: the elastic settle keeps its full motion,
    // but once its oscillation drops below a pixel there's nothing sub-pixel left to
    // shimmer on (that residual shimmer was the "micro jitter after it stops"). The
    // visible bounces are all larger than a pixel, so the dynamism is untouched.
    const yR = Math.round(g.y);
    const cY = Math.round(g.y + bend.current.v + flex.current.v);
    if (pathRef.current) pathRef.current.setAttribute("d", `M ${g.x0} ${yR} Q ${cx} ${cY} ${g.x1} ${yR}`);
    // headline peel: reveal the headline above the line, exposing the source behind
    // it. the reveal uses bend only (not flex) so the landing twang never flickers.
    const hl = hlRef.current;
    if (hl && g.hlW) {
      const ly = g.y + dropY.current.v, bendR = bend.current.v;
      const hw = g.hlW + 16, lyl = ly - g.hlTop;
      const A = g.hlL - 2 * cx + g.x1, B = 2 * (cx - g.hlL);
      let d = "M 0 0 L " + hw + " 0";
      for (let i = 18; i >= 0; i--) {
        const xp = (hw * i) / 18, X = g.hlL + xp;
        let t;
        if (Math.abs(A) < 1e-4) { t = (X - g.hlL) / B; }
        else {
          const C = g.hlL - X;
          let disc = B * B - 4 * A * C; if (disc < 0) disc = 0;
          const sq = Math.sqrt(disc);
          t = (-B + sq) / (2 * A);
          if (t < 0 || t > 1) t = (-B - sq) / (2 * A);
        }
        if (t < 0) t = 0; else if (t > 1) t = 1;
        d += " L " + xp + " " + Math.round(lyl + 2 * t * (1 - t) * bendR);
      }
      hl.style.clipPath = "path('" + d + " Z')";
    }
    // the eyebrow is revealed content too: reveal the part of it above the line's
    // current height, so the falling line wipes it in on its way to the headline
    // (hidden until the line reaches it; fully shown once the line is past it)
    const ev = eyebrowRef.current;
    if (ev && g.evH) {
      const rel = g.y + dropY.current.v - g.evTop;
      const shown = rel < 0 ? 0 : rel > g.evH ? g.evH : rel;
      ev.style.clipPath = `inset(0 0 ${Math.round(g.evH - shown)}px 0)`;
    }
  };

  const measure = () => {
    const area = areaRef.current;
    const p0 = panelRefs.current[0];
    if (!area || !p0) return;
    const unit = p0.querySelector("[data-hero-unit]");
    if (!unit) return;
    unitRef.current = unit;
    gRef.current = unit.querySelector("[data-hero-line-g]");
    pathRef.current = unit.querySelector("[data-hero-path]");
    capLRef.current = unit.querySelector("[data-hero-cap-l]");
    capRRef.current = unit.querySelector("[data-hero-cap-r]");
    hlRef.current = unit.querySelector("[data-hero-headline]");
    codeRef.current = unit.querySelector("[data-hero-code]");
    const svg = unit.querySelector("[data-hero-line]");

    const vw = area.clientWidth;
    const ar = area.getBoundingClientRect();
    const ur = unit.getBoundingClientRect();
    const uw = unit.clientWidth, uh = unit.clientHeight;
    // line geometry in the unit's own coordinates
    const acc = unit.querySelector(".accent");
    let x0 = uw * 0.02, y = uh;
    if (acc) {
      const r = acc.getBoundingClientRect();
      x0 = r.left - ur.left;
      y = r.bottom - ur.top + 8;
    }
    const x1 = uw - 24;
    const xm = (x0 + x1) / 2;
    const sky = y + 380;
    // the unit's layout offset within the area (undo the strip's camera shift so
    // the pointer math maps cursor -> unit coordinates)
    const ux = ur.left - ar.left + camRef.current.w;
    const uy = ur.top - ar.top;
    geom.current = { vw, x0, x1, xm, y, sky, ux, uy };
    if (svg) svg.setAttribute("viewBox", `0 0 ${uw} ${uh}`);
    const cap = (el, x) => { if (el) { el.setAttribute("x1", x); el.setAttribute("x2", x); el.setAttribute("y1", y - 6); el.setAttribute("y2", y + 6); } };
    cap(capLRef.current, x0);
    cap(capRRef.current, x1);
    const hl = hlRef.current;
    if (hl) {
      const hr2 = hl.getBoundingClientRect();
      geom.current.hlL = hr2.left - ur.left;
      geom.current.hlTop = hr2.top - ur.top;
      geom.current.hlH = hr2.height;
      geom.current.hlW = hl.clientWidth;
    }
    // the eyebrow lives above the unit; measure it in the same unit-relative space so
    // the line's height can wipe it in as it passes on the way down
    eyebrowRef.current = p0.querySelector("[data-hero-eyebrow]");
    if (eyebrowRef.current) {
      const evr = eyebrowRef.current.getBoundingClientRect();
      geom.current.evTop = evr.top - ur.top;
      geom.current.evH = evr.height;
    }
    // size the source to fill the headline height, so both scale as one
    if (codeRef.current && geom.current.hlH) {
      const lines = (codeRef.current.textContent.match(/\n/g) || []).length + 1;
      codeRef.current.style.fontSize = (geom.current.hlH * CODE_FILL) / (lines * CODE_LINE_HEIGHT) + "px";
    }
    ctrlX.current.v = xm;
    camRef.current.w = activeRef.current * vw;
    draw();
  };

  // restore the clipping the shuffle lifted (called as the line takes over)
  const reclip = () => {
    if (codeRef.current) codeRef.current.style.overflow = "hidden";
    if (panelRefs.current[0]) panelRefs.current[0].style.overflow = "";
  };

  // collect the React-rendered per-word spans (the source is split in JSX so the
  // inline-block survives re-renders and the transforms actually apply)
  const collectWords = () => {
    const code = codeRef.current || (unitRef.current && unitRef.current.querySelector("[data-hero-code]"));
    if (!code || wordsRef.current) return;
    codeRef.current = code;
    const spans = code.querySelectorAll(".cw");
    if (spans.length) wordsRef.current = Array.from(spans);
  };

  // the source shuffles into place from across the whole screen
  const shuffleIn = (delay) => {
    collectWords();
    const words = wordsRef.current, code = codeRef.current;
    if (!words || !code) return;
    gsap.killTweensOf(words);
    gsap.set(words, { clearProps: "all" });
    gsap.set(code, { opacity: 1 });
    if (prefersReduced()) { code.style.overflow = "hidden"; return; }
    // unclip the code box and the panel so the words fly in from across the whole
    // screen; the hero area stays clipped so they can't create a page scrollbar
    code.style.overflow = "visible";
    if (panelRefs.current[0]) panelRefs.current[0].style.overflow = "visible";
    gsap.from(words, {
      x: () => gsap.utils.random(-window.innerWidth * 0.6, window.innerWidth * 0.6),
      y: () => gsap.utils.random(-window.innerHeight * 0.55, window.innerHeight * 0.55),
      opacity: 0,
      rotation: () => gsap.utils.random(-15, 15),
      stagger: { amount: 1.1, from: "random" },
      duration: 0.9,
      ease: "power3.out",
      delay: delay || 0,
    });
  };

  // the line drops from the sky, reclips the source behind the headline, and bows
  // once on impact (the twang lives in flex so it never flickers the reveal)
  const dropIn = (delay) => {
    measure();
    const g = geom.current;
    if (prefersReduced()) {
      dropY.current.v = 0; bend.current.v = 0; flex.current.v = 0; ctrlX.current.v = g.xm;
      reclip();
      if (gRef.current) gsap.set(gRef.current, { opacity: 1 });
      draw();
      interactiveRef.current = true;
      return;
    }
    dropY.current.v = -g.sky; bend.current.v = 0; flex.current.v = 0; ctrlX.current.v = g.xm; draw();
    if (lineTl.current) lineTl.current.kill();
    const tl = gsap.timeline({
      delay: delay || 0,
      onStart: () => { reclip(); if (gRef.current) gsap.set(gRef.current, { opacity: 1 }); },
      onComplete: () => { interactiveRef.current = true; },
    });
    const impactFlex = Math.min((g.x1 - g.x0) * 0.09, 90);
    tl.to(dropY.current, { v: 0, duration: 0.86, ease: "power2.in" }, 0);
    tl.to(flex.current, { v: impactFlex, duration: 0.12, ease: "expo.out" }, 0.80);
    tl.to(flex.current, { v: 0, duration: 0.82, ease: "elastic.out(1.5,0.4)" }, 0.92);
    lineTl.current = tl;
  };

  // interactive peel — the control point grabs the cursor as it crosses the line,
  // elastic-snapping back to straight on release (harder snap from a bigger lift)
  const release = () => {
    if (!connectedRef.current) return;
    connectedRef.current = false;
    gsap.killTweensOf(bend.current);
    gsap.killTweensOf(ctrlX.current);
    const g = geom.current;
    const f = Math.min(1, Math.abs(bend.current.v) / (2 * (g.hlH - 8)));
    const amp = (1 + f * 0.2).toFixed(2); // lower overshoot so a full-apex peel dips less on the way back
    const period = (0.34 - f * 0.05).toFixed(2);
    const dur = 0.7 + f * 0.275;
    // spring only the bend flat — leave the control x under where the cursor left it.
    // animating x back to centre here made the peak sweep across to the middle as it
    // released (opposite to a drag that ended on one side). once flat, bend is 0 so x
    // is invisible; reset it centred then, for the next interaction.
    gsap.to(bend.current, {
      v: 0, duration: dur, ease: `elastic.out(${amp},${period})`,
      onComplete: () => { ctrlX.current.v = g.xm; },
    });
  };

  const onPointer = (e) => {
    if (!interactiveRef.current) return;
    if (e.pointerType && e.pointerType !== "mouse") return;
    const area = areaRef.current;
    if (!area) return;
    const g = geom.current;
    const rect = area.getBoundingClientRect();
    const cx = e.clientX - rect.left - g.ux; // cursor in unit coordinates
    const cy = e.clientY - rect.top - g.uy;
    const within = cx > g.x0 - 30 && cx < g.x1 + 10;
    const dy = cy - g.y;
    if (!connectedRef.current && within && Math.abs(dy) < 10) {
      connectedRef.current = true;
      gsap.killTweensOf(bend.current);
      gsap.killTweensOf(ctrlX.current);
    }
    if (connectedRef.current) {
      const upCap = -2 * (g.hlH + 115); // headroom above the headline
      const capCy = g.y + upCap / 2;
      // release as soon as the cursor pulls clearly off the line — a tight, even margin
      // (tighter than the grab zone) so it snaps back promptly instead of clinging out
      // to the far edges. left used to reach 30px past the end, which felt stickiest.
      if (cx < g.x0 - 8 || cx > g.x1 + 8 || cy > g.y + 50 || cy < capCy - 55) { release(); return; }
      let b = 2 * dy;
      if (b > 90) b = 90;          // cap the downward dip
      if (b < upCap) b = upCap;    // cap the upward rise
      bend.current.v = b;
      ctrlX.current.v = cx < g.x0 ? g.x0 : cx > g.x1 ? g.x1 : cx; // peak follows the cursor
    }
  };

  // reset the line straight and at rest with the source hidden behind the headline,
  // so navigation pans the finished hero rather than replaying the drop
  const settleRest = () => {
    if (lineTl.current) lineTl.current.kill();
    gsap.killTweensOf([bend.current, ctrlX.current, flex.current, dropY.current]);
    connectedRef.current = false;
    bend.current.v = 0; flex.current.v = 0; ctrlX.current.v = geom.current.xm; dropY.current.v = 0;
    reclip();
    if (gRef.current) gsap.set(gRef.current, { opacity: 1 });
  };

  const setInteractive = (v) => { interactiveRef.current = v; };

  useLayoutEffect(() => {
    measure();
    // a home first-load plays the intro (line hidden until it drops, source hidden
    // until it shuffles); a deep link elsewhere or reduced-motion skips it and rests
    // the line at its finished position (panned off-screen until the camera is home)
    if (activeRef.current === 0 && !prefersReduced()) {
      if (gRef.current) gsap.set(gRef.current, { opacity: 0 });
      if (codeRef.current) gsap.set(codeRef.current, { opacity: 0 });
    } else {
      dropY.current.v = 0;
      if (gRef.current) gsap.set(gRef.current, { opacity: 1 });
      reclip();
      draw();
    }
    gsap.ticker.add(draw);
    const areaEl = areaRef.current;
    if (areaEl) { areaEl.addEventListener("pointermove", onPointer); areaEl.addEventListener("pointerleave", release); }
    const ro = () => measure();
    window.addEventListener("resize", ro);
    // `alive` guards the deferred kickoff: React 18 StrictMode runs setup ->
    // cleanup -> setup, and a torn-down setup's pending fonts.ready callback must
    // not fire a second intro (which would re-scatter words that already landed)
    let did = false, alive = true;
    const kickoff = () => {
      if (did || !alive) return; did = true;
      measure();
      if (activeRef.current === 0) { shuffleIn(0); dropIn(1.35); }
      if (onReady) onReady(activeRef.current);
    };
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(kickoff);
    const t = setTimeout(kickoff, 500);
    return () => {
      alive = false;
      gsap.ticker.remove(draw);
      if (areaEl) { areaEl.removeEventListener("pointermove", onPointer); areaEl.removeEventListener("pointerleave", release); }
      window.removeEventListener("resize", ro);
      clearTimeout(t);
      if (wordsRef.current) gsap.killTweensOf(wordsRef.current);
      if (lineTl.current) lineTl.current.kill();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { draw, settleRest, setInteractive };
}
